import React, { useEffect, useRef, useState } from 'react';
import { Dialog, DialogContent, DialogHeader, DialogTitle } from './ui/dialog';
import { Button } from './ui/button';
import { Printer, Download, Loader2, ExternalLink, FileText, Share2 } from 'lucide-react';
import { toast } from 'sonner';
import { warn as logWarn } from '../lib/logger';

/**
 * Reusable PDF preview dialog.
 * - Loads the PDF via fetch with `credentials: 'include'` (Phase 22: cookie-based auth).
 * - Displays in an iframe (browser native viewer with its own print button).
 * - Provides explicit "Imprimir" and "Descargar" buttons at the top.
 *
 * Props:
 *  - open, onOpenChange: dialog controls
 *  - url: absolute URL to fetch the PDF from
 *  - filename: suggested filename for download
 *  - title: dialog header
 */
/** Safari de iPhone/iPad no renderiza un PDF dentro de un iframe (ni con
 *  blob:): sale un recuadro en blanco o negro. En pantallas pequeñas no se
 *  usa el iframe — se ofrece abrirlo en el visor nativo del móvil, que sí
 *  funciona y además permite compartir y guardar en Archivos. */
function usaVisorNativo() {
  if (typeof window === 'undefined') return false;
  return window.matchMedia('(max-width: 640px), (pointer: coarse)').matches;
}

export default function PdfPreviewDialog({ open, onOpenChange, url, filename = 'documento.pdf', title = 'Vista previa del documento' }) {
  const [blobUrl, setBlobUrl] = useState(null);
  const [blobFile, setBlobFile] = useState(null);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState(null);
  const [movil, setMovil] = useState(usaVisorNativo);
  const [compartiendo, setCompartiendo] = useState(false);
  const iframeRef = useRef(null);

  useEffect(() => {
    const mq = window.matchMedia('(max-width: 640px), (pointer: coarse)');
    const onChange = () => setMovil(mq.matches);
    mq.addEventListener('change', onChange);
    return () => mq.removeEventListener('change', onChange);
  }, []);

  useEffect(() => {
    let currentUrl = null;
    let cancelled = false;
    // También en móvil: el blob se baja al abrir para que «Compartir o
    // guardar» pueda llamar a navigator.share sin ningún await previo (la
    // activación del gesto en Safari caduca a los 5 s; revisión 2026-09-02).
    if (open && url) {
      setLoading(true);
      setError(null);
      setBlobUrl(null);
      // Phase 22: la cookie httpOnly viaja automáticamente con credentials:'include'.
      fetch(url, { credentials: 'include' })
        .then((r) => {
          if (!r.ok) throw new Error(`Error ${r.status}`);
          return r.blob();
        })
        .then((blob) => {
          if (cancelled) return;
          currentUrl = URL.createObjectURL(blob);
          setBlobUrl(currentUrl);
          setBlobFile(new File([blob], filename, { type: 'application/pdf' }));
        })
        .catch((e) => {
          if (!cancelled) setError(e.message || 'Error cargando el PDF');
        })
        .finally(() => {
          if (!cancelled) setLoading(false);
        });
    }
    return () => {
      cancelled = true;
      if (currentUrl) URL.revokeObjectURL(currentUrl);
    };
  }, [open, url, movil, filename]);

  /**
   * Print the PDF.
   *
   * Why we go through `window.open` instead of `iframe.contentWindow.print()`:
   * when the browser prints a PDF embedded in an iframe, it uses the iframe's
   * rendered zoom — which can scale the document differently than expected
   * and ignore the PDF's internal `@page size: A4`. Printing the same PDF
   * opened in a top-level tab respects the A4 size natively (this is
   * exactly what happens when the user downloads + opens + prints manually).
   *
   * So the primary path is: open the blob in a new tab and call `print()`
   * once it has loaded. We fall back to the iframe approach only when popups
   * are blocked.
   */
  const handlePrint = () => {
    if (!blobUrl) return;
    const w = window.open(blobUrl, '_blank');

    if (w) {
      // The PDF viewer in the new tab needs a moment to render before we can
      // trigger the print dialog. We try at increasing delays in case the
      // first attempt fires too early.
      const tryPrint = (delay) => setTimeout(() => {
        try {
          w.focus();
          w.print();
        } catch (err) {
          // logWarn is no-op in production; only logs in dev mode.
          logWarn('[PdfPreview] new-tab print failed, user can still print manually:', err?.message || err);
        }
      }, delay);
      tryPrint(700);
      return;
    }

    // Popups blocked: fall back to in-iframe print. Quality is lower (the
    // browser may not respect the embedded @page size) but the user at least
    // gets the print dialog without leaving the app.
    logWarn('[PdfPreview] window.open blocked; falling back to iframe print');
    try {
      iframeRef.current?.contentWindow?.focus();
      iframeRef.current?.contentWindow?.print();
    } catch (err) {
      logWarn('[PdfPreview] iframe print also failed:', err?.message || err);
    }
  };

  const handleDownload = () => {
    if (!blobUrl) return;
    const a = document.createElement('a');
    a.href = blobUrl;
    a.download = filename;
    document.body.appendChild(a);
    a.click();
    document.body.removeChild(a);
  };

  /** iPhone/iPad (2026-09-02): con la app instalada como icono
   *  (PWA), window.open abre el PDF en una vista SIN la barra de Safari —
   *  sin compartir ni guardar, sin botones. La salida buena en móvil es la
   *  hoja nativa de compartir (Web Share API con el archivo): WhatsApp,
   *  AirDrop, Guardar en Archivos, Imprimir… Funciona también dentro de la
   *  app instalada. */
  const compartirMovil = async () => {
    if (compartiendo) return;
    setCompartiendo(true);
    try {
      let file = blobFile;
      if (!file) {
        const r = await fetch(url, { credentials: 'include' });
        if (!r.ok) throw new Error(`Error ${r.status}`);
        file = new File([await r.blob()], filename, { type: 'application/pdf' });
      }
      if (navigator.canShare && navigator.canShare({ files: [file] })) {
        await navigator.share({ files: [file], title: filename });
        return;
      }
      // Navegador sin compartir archivos: descarga clásica con blob.
      const bu = URL.createObjectURL(file);
      const a = document.createElement('a');
      a.href = bu;
      a.download = filename;
      document.body.appendChild(a);
      a.click();
      a.remove();
      setTimeout(() => URL.revokeObjectURL(bu), 30000);
    } catch (e) {
      if (e?.name === 'AbortError') return;
      if (e?.name === 'NotAllowedError') {
        toast.error('El teléfono no ha dejado abrir el menú de compartir. Vuelve a pulsar el botón.');
      } else {
        toast.error(e?.message || 'No se pudo preparar el documento');
      }
    } finally {
      setCompartiendo(false);
    }
  };

  const openInNewTab = () => {
    if (blobUrl) window.open(blobUrl, '_blank');
  };

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="max-w-5xl w-[95vw] h-[92vh] flex flex-col p-0 gap-0">
        <DialogHeader className="px-4 py-3 border-b border-border space-y-0">
          <div className="flex flex-wrap items-center justify-between gap-2">
            <DialogTitle className="text-sm sm:text-base font-display font-semibold text-[#13231E] truncate min-w-0 flex-1">{title}</DialogTitle>
            {!movil && (
              <div className="flex items-center gap-2 shrink-0">
                <Button size="sm" variant="outline" onClick={handlePrint} disabled={!blobUrl} data-testid="pdf-preview-print-button">
                  <Printer className="w-4 h-4 mr-1.5" /> Imprimir
                </Button>
                <Button size="sm" variant="outline" onClick={handleDownload} disabled={!blobUrl} data-testid="pdf-preview-download-button">
                  <Download className="w-4 h-4 mr-1.5" /> Descargar
                </Button>
                <Button size="sm" variant="ghost" onClick={openInNewTab} disabled={!blobUrl} title="Abrir en nueva pestaña">
                  <ExternalLink className="w-4 h-4" />
                </Button>
              </div>
            )}
          </div>
        </DialogHeader>
        <div className="flex-1 bg-[#f3f4f6] overflow-hidden">
          {movil ? (
            <div className="h-full flex flex-col items-center justify-center gap-3 px-6 text-center">
              <FileText className="w-12 h-12 text-[#2E6E5E]" />
              <div className="text-sm font-medium text-[#13231E] break-words max-w-full">{filename}</div>
              <p className="text-xs text-muted-foreground max-w-xs">
                «Compartir o guardar» abre el menú del teléfono: WhatsApp,
                AirDrop, Guardar en Archivos, Imprimir…
              </p>
              <Button
                className="bg-[#2E6E5E] hover:bg-[#1E4A3F] h-11 px-6"
                onClick={compartirMovil}
                disabled={compartiendo || (loading && !blobFile)}
                data-testid="pdf-preview-compartir-movil"
              >
                {compartiendo
                  ? <Loader2 className="w-4 h-4 mr-1.5 animate-spin" />
                  : <Share2 className="w-4 h-4 mr-1.5" />}
                Compartir o guardar
              </Button>
              <button
                type="button"
                onClick={() => window.open(url, '_blank', 'noopener')}
                className="text-xs text-[#2E6E5E] underline underline-offset-2"
                data-testid="pdf-preview-abrir-movil"
              >
                Abrir solo para ver
              </button>
            </div>
          ) : loading ? (
            <div className="h-full flex items-center justify-center">
              <Loader2 className="w-8 h-8 animate-spin text-[#2E6E5E]" />
            </div>
          ) : error ? (
            <div className="h-full flex flex-col items-center justify-center gap-2 text-center px-6">
              <div className="text-sm text-destructive font-semibold">No se pudo cargar el PDF</div>
              <div className="text-xs text-muted-foreground">{error}</div>
            </div>
          ) : blobUrl ? (
            <iframe
              ref={iframeRef}
              src={blobUrl}
              title={title}
              className="w-full h-full border-0 bg-white"
              data-testid="pdf-preview-iframe"
            />
          ) : null}
        </div>
      </DialogContent>
    </Dialog>
  );
}
